<template>
  <Modal :open="open" size="medium" @close="closeModal">
    <div class="flex justify-between mb-4">
      <h1 class="text-gray-900 font-bold">Échanger votre produit</h1>
    </div>

    <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <form class="space-y-4" @submit="handleSubmit($event, handleExchangeProduct)" ref="ExchangeForm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div>
            <label for="title" class="block text-sm text-gray-700">
              Nom du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="title"
                name="title"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Nom du produit"
                v-model="title"
              />
              <ErrorMessage name="title" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="price" class="block text-sm text-gray-700">
              Prix du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                id="price"
                name="price"
                type="text"
                class="pr-16 shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="249,99"
                v-model="priceFormatted"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm" id="price-currency">{{ currencyCode }}</span>
              </div>
              <ErrorMessage name="price" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="referenceId" class="block text-sm text-gray-700">
              Numéro de série ou IMEI
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="referenceId"
                name="referenceId"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="35478405412998702"
                v-model="referenceId"
              />
              <ErrorMessage name="referenceId" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="purchaseDate" class="block text-sm text-gray-700">
              Date d'achat
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                type="date"
                name="purchaseDate"
                id="purchaseDate"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="purchaseDate"
              />
              <ErrorMessage name="purchaseDate" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
        </div>
        <div class="mt-6">
          <Checkbox
            id="exchangeDeclarationCompleted"
            name="exchangeDeclarationCompleted"
            class="mt-5"
            label="Je certifie que les informations fournies dans cette déclaration sont exactes et complètes à ma connaissance. Je comprends que toute fausse déclaration ou omission pourrait entraîner l'annulation de ma couverture ou le refus d'une indemnisation. En cochant cette case, je m'engage à ce que cette déclaration soit conforme à la réalité."
            v-model="exchangeDeclarationCompleted"
          />
          <div v-if="!exchangeDeclarationCompleted" class="mt-2 text-sm text-red-600">
            Veuillez cocher la case ci-dessus.
          </div>
          <p class="bg-gray-100 italic text-sm mt-4 p-3 rounded-lg">
            Je comprends que toute fausse déclaration ou omission pourrait entraîner l'annulation de ma couverture ou le
            refus d'une indemnisation. En cochant cette case, je m'engage à ce que cette déclaration soit conforme à la
            réalité. Nous rappelons à l'ensemble de nos clients que l'article 313-1 du Code pénal prévoit qu'une fraude
            à l'assurance peut être punie d'une amende allant jusqu’à 375 000 € et d'une peine d'emprisonnement de 5
            ans.
          </p>
        </div>
        <div class="mt-6">
          <div class="flex flex-row gap-4">
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent px-4 py-2 text-lg font-medium text-button-font-primary bg-primary uppercase transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                type="submit"
              >
                {{ $t("Échanger") }}
              </button>
            </div>

            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent bg-red-100 px-4 py-2 text-lg font-medium text-red-700 transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                type="button"
                @click="closeModal"
              >
                {{ $t("Close") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </VeeForm>
  </Modal>
</template>

<script>
import { Modal, Checkbox } from "@estaly/ui";
import { ErrorMessage, Field, Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import { mapActions } from "vuex";

export default {
  components: {
    Modal,
    Checkbox,
    ErrorMessage,
    Field,
    VeeForm,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      required: true,
    },
  },

  data() {
    const schema = yup.object({
      referenceId: yup.string().required("Le numéro de série est requis"),
      title: yup.string().required("Le nom du produit est requis"),
      price: yup
        .string()
        .required("Le prix du produit est requis.")
        .matches(/^\d+(\.\d{1,2})?$|^\d+(,\d{1,2})?$/, "Le format du prix est invalide."),
      purchaseDate: yup
        .date()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .max(new Date(new Date().setHours(23, 59, 59, 999)), "La date d'achat ne peut pas être dans le futur")
        .min(
          new Date(new Date().setDate(new Date().getDate() - 30)),
          "La date d'achat ne peut être supérieure à 30 jours",
        )
        .required("La date d'achat est requise")
        .typeError("La date d'achat est invalide"),
      exchangeDeclarationCompleted: yup.boolean().oneOf([true], "Veuillez cocher la case ci-dessus"),
    });

    return {
      schema,
      referenceId: "",
      title: "",
      price: "",
      purchaseDate: "",
      exchangeDeclarationCompleted: false,
    };
  },

  computed: {
    priceFormatted: {
      get() {
        return this.price === undefined ? "" : this.price.toString().replace(".", ",");
      },
      set(value) {
        this.price = value.replace(",", ".");
      },
    },
  },

  methods: {
    ...mapActions("planSales", ["declareProductExchange"]),
    ...mapActions("notifications", ["notify"]),

    closeModal() {
      this.referenceId = "";
      this.title = "";
      this.price = "";
      this.purchaseDate = "";
      this.exchangeDeclarationCompleted = false;
      this.$emit("close");
    },

    async handleExchangeProduct() {
      if (!this.exchangeDeclarationCompleted) return;
      const response = await this.declareProductExchange({
        friendlyId: this.plan.friendlyId,
        referenceId: this.referenceId,
        title: this.title,
        price: this.price,
        purchaseDate: this.purchaseDate,
      });

      if (response?.success) {
        await this.notify({
          category: "simple",
          type: "success",
          title: "L'échange de produit a été effectué",
        });
        this.$emit("refreshPlanSale");
        this.closeModal();
      }
    },
  },
};
</script>
